import {
  CarOutlined,
  CoffeeOutlined,
  HomeOutlined,
  SunOutlined,
} from '@ant-design/icons';
import { Form } from 'antd';
import {
  CurrencySelect,
  FieldCard,
  Group,
  ValueSelect,
} from '@/components/profile/settingCards';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';
import { allowanceCurrencyRule } from './allowanceCurrencyRule';

/**
 * The "Allowances" tile of the company profile as cards: one per allowance,
 * calculation method and currency in one row. Same fields, rules and save as
 * CompanyAllowancesFields, which steps aside on the profile.
 */
export default function CompanyAllowancesProfilePanel() {
  const ctx = useCompanyGeneralDataForm();
  const {
    currencyOptions,
    getTranslatedCalculationOptionsForType,
    hasBranchCompanyType,
    readOnly,
    sectionClass,
    shouldWorkFromHomeBeVisible,
    t,
  } = ctx;
  const f = (key) => t(key, { ns: 'forms' });
  const lock = (state) => readOnly || !!state?.disabled;
  const state = (name) => ctx[`${name}State`];
  const keys = (name) => ctx[`${name}Keys`];

  // `name` = allowance prefix (mealAllowance…), `type` = its method options.
  const allowance = (name, type, { icon, method, currency }) => {
    const methodState = state(name);
    const currencyState = state(`${name}Currency`);
    if (!methodState?.visible && !currencyState?.visible) return null;
    return (
      <FieldCard icon={icon} title={f(method)}>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-[42px] text-[12px] text-zinc-500">
          {methodState?.visible && (
            <Form.Item
              name={keys(name)}
              className="!mb-0"
              rules={[
                {
                  required: true,
                  message: f('formValidation.selectRequired'),
                },
              ]}
            >
              <ValueSelect
                allowClear
                locked={lock(methodState)}
                className="w-56"
                placeholder={f(method)}
                options={getTranslatedCalculationOptionsForType(type, t)}
              />
            </Form.Item>
          )}
          {currencyState?.visible && (
            <span className="flex items-center gap-2">
              {f(currency)}
              {/* Required once the allowance is in use: one paid in no
                  currency reaches the payroll without one. */}
              <Form.Item
                name={keys(`${name}Currency`)}
                className="!mb-0"
                dependencies={[keys(name)]}
                rules={[
                  allowanceCurrencyRule(
                    keys(name),
                    f('formValidation.currencyRequired')
                  ),
                ]}
              >
                <CurrencySelect
                  locked={lock(currencyState)}
                  options={currencyOptions}
                  className="w-28"
                />
              </Form.Item>
            </span>
          )}
        </div>
      </FieldCard>
    );
  };

  if (hasBranchCompanyType) return null;

  return (
    <div className={sectionClass('naknade')}>
      <Group>
        {allowance('mealAllowance', 'meal', {
          icon: <CoffeeOutlined />,
          method: 'calculationMethodMeal',
          currency: 'mealCurrency',
        })}
        {allowance('holidayAllowance', 'holiday', {
          icon: <SunOutlined />,
          method: 'calculationMethodHoliday',
          currency: 'holidayCurrency',
        })}
        {allowance('travelAllowance', 'travel', {
          icon: <CarOutlined />,
          method: 'travelExpensesMethod',
          currency: 'travelExpCurrency',
        })}
        {shouldWorkFromHomeBeVisible &&
          allowance('workFromHomeAllowance', 'workFromHome', {
            icon: <HomeOutlined />,
            method: 'workFromHomeMethod',
            currency: 'workFromHomeCurrency',
          })}
      </Group>
    </div>
  );
}
